/**
 * IANA timezone list helpers.
 *
 * Prefer the runtime-native `Intl.supportedValuesOf('timeZone')` when
 * available (Node 20+, modern browsers). Fall back to a frozen list for
 * older Capacitor WebViews (iOS < 15.4) that predate the API.
 *
 * The frozen list is generated from Node's current output; it intentionally
 * lags real-time tzdata — that's fine because:
 *   - The authoritative render (venue wall-clock) is always server-side, so
 *     it uses full ICU regardless of the device.
 *   - Client-side falls back to the device's local zone if the saved zone
 *     is unrecognized.
 */

// Minimal fallback — covers the core working set. Regenerate from
// `Intl.supportedValuesOf('timeZone')` at release time if the shape changes.
// Keep this list alphabetized.
const FROZEN_IANA_LIST: readonly string[] = Object.freeze([
  'Africa/Abidjan',
  'Africa/Algiers',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Tunis',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Cordoba',
  'America/Asuncion',
  'America/Bogota',
  'America/Caracas',
  'America/Chicago',
  'America/Costa_Rica',
  'America/Denver',
  'America/Edmonton',
  'America/El_Salvador',
  'America/Godthab',
  'America/Guatemala',
  'America/Halifax',
  'America/Havana',
  'America/La_Paz',
  'America/Lima',
  'America/Los_Angeles',
  'America/Managua',
  'America/Mazatlan',
  'America/Mexico_City',
  'America/Montevideo',
  'America/New_York',
  'America/Nuuk',
  'America/Panama',
  'America/Phoenix',
  'America/Puerto_Rico',
  'America/Regina',
  'America/Santiago',
  'America/Santo_Domingo',
  'America/Sao_Paulo',
  'America/St_Johns',
  'America/Tegucigalpa',
  'America/Tijuana',
  'America/Toronto',
  'America/Vancouver',
  'America/Winnipeg',
  'Asia/Baghdad',
  'Asia/Bangkok',
  'Asia/Beirut',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Kuala_Lumpur',
  'Asia/Manila',
  'Asia/Riyadh',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Taipei',
  'Asia/Tehran',
  'Asia/Tokyo',
  'Atlantic/Azores',
  'Atlantic/Canary',
  'Atlantic/Reykjavik',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Kyiv',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Europe/Zurich',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Honolulu',
  'UTC',
])

// Retired-zone aliases. The IANA tzdb occasionally retires a zone name
// (e.g., America/Godthab → America/Nuuk, Europe/Kiev → Europe/Kyiv).
// Older clients may still submit the old name. We accept it as an alias
// at validation time and rewrite at format time in safeFormatInTimeZone.
const RETIRED_ZONE_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  'America/Godthab': 'America/Nuuk',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Europe/Kiev': 'Europe/Kyiv',
  'Europe/Nicosia': 'Asia/Nicosia',
})

// Module-scoped — built once per process. `Intl.supportedValuesOf` is spec'd
// stable; we never rebuild the set between requests.
const supportedZones: ReadonlySet<string> = (() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const intl = Intl as any
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      return new Set<string>(intl.supportedValuesOf('timeZone') as string[])
    } catch {
      // Fall through to frozen list
    }
  }
  return new Set<string>(FROZEN_IANA_LIST)
})()

export function listSupportedTimezones(): string[] {
  return Array.from(supportedZones).sort()
}

export function isValidTimezone(tz: string | null | undefined): tz is string {
  if (!tz) return false
  if (supportedZones.has(tz)) return true
  if (RETIRED_ZONE_ALIASES[tz] && supportedZones.has(RETIRED_ZONE_ALIASES[tz])) return true
  // Runtime fallback: some Node/ICU builds know Argentina zones (e.g.
  // `America/Argentina/Buenos_Aires`) via the formatter API even when
  // `Intl.supportedValuesOf('timeZone')` hasn't been refreshed to include
  // them. If `DateTimeFormat` constructs without throwing, the zone is
  // runtime-usable, so accept it. Fake strings like `Not/Real` will throw
  // a RangeError and be correctly rejected.
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export function canonicalizeTimezone(tz: string): string {
  return RETIRED_ZONE_ALIASES[tz] ?? tz
}

export function detectBrowserTimezone(): string | null {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return null
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return tz || null
  } catch {
    return null
  }
}
